import { ComponentPropsWithoutRef, useContext, useEffect, useRef } from 'react';
import { FilterContext, FilterSearchKey } from 'components/filter/filters-next/FilterContext';
import { FilterIconStrategy } from 'components/filter/filters-next/FilterIconStrategy';
import { FilterListItemButton } from 'components/filter/filters-next/FilterListItemButton';
import { FilterSectionContext } from 'components/filter/filters-next/FilterSectionContext';
import { LabelStrategy } from 'components/filter/filters-next/LabelStrategy';
import { RelationalServerKey, ServerKey } from 'components/filter/types';
import Fuse from 'fuse.js';

interface FilterListItemProps extends ComponentPropsWithoutRef<'button'> {
    serverKey: ServerKey;
    relationalServerKey?: RelationalServerKey;
    isLegacy?: boolean;
}

const FilterListItem = (props: FilterListItemProps) => {
    const { serverKey, relationalServerKey, isLegacy = true, onClick, children, ...rest } = props;
    const {
        search,
        searchItems,
        searchInstance,
        setSelectedFilter,
        setRelationalServerKey,
        setIsLegacy,
    } = useContext(FilterContext);
    const { resource } = useContext(FilterSectionContext);
    const ref = useRef<HTMLButtonElement>(null);
    const isSearchEmpty = search.length === 0 && searchItems.length === 0;
    const isSearchMatch =
        isSearchEmpty ||
        searchItems.find(
            result => result.item.serverKey === serverKey && result.item.resource === resource,
        );

    useEffect(() => {
        const scopedServerKey = `${serverKey}-${resource}`;
        const index = searchInstance.getIndex() as Fuse.FuseIndex<FilterSearchKey> & {
            docs: FilterSearchKey[];
        };
        const isIndexed = !!index.docs.find(d => d.scopedServerKey === scopedServerKey);
        if (!isIndexed) {
            searchInstance.add({
                serverKey,
                scopedServerKey,
                label: ref.current?.innerText ?? '',
                resource,
            });
        }
    }, [searchInstance, serverKey, resource]);

    if (isSearchMatch) {
        return (
            <FilterListItemButton
                onClick={e => {
                    setSelectedFilter(serverKey);
                    setIsLegacy(isLegacy);
                    if (relationalServerKey) {
                        setRelationalServerKey(relationalServerKey);
                    }
                    onClick?.(e);
                }}
                {...rest}
            >
                <div
                    className={
                        'flex size-4 items-center justify-center [&>svg]:h-full [&>svg]:w-full'
                    }
                >
                    <FilterIconStrategy
                        serverKey={serverKey}
                        relationalServerKey={relationalServerKey}
                    />
                </div>
                {children ? (
                    <span ref={ref}>{children}</span>
                ) : (
                    <LabelStrategy
                        ref={ref}
                        strategy={serverKey}
                        relationalServerKey={relationalServerKey}
                    />
                )}
            </FilterListItemButton>
        );
    }

    return null;
};

export { FilterListItem };
export type { FilterListItemProps };
